import type { Step } from '@/app/(platform)/(subscription-gated)/labs/_engine/steps';

/**
 * The PUBLIC PREVIEW's step sequence, a strict SUBSET of the sprint's 18, in the same order.
 *
 * 🔴 `m2-results` IS OUT, AND THAT IS A BUG FIX (Jay, 2026-08-27). Adding M3 after the archetype
 * reveal meant the reader was told their result at step 7 of 10 and then asked to keep working, with
 * the reveal's own footer promising "your result on the next screen" when the next screen was a
 * practice table. It also showed the archetype twice, once here and once on the payoff.
 *
 * The reveal now happens ONCE, on the payoff, after every piece of work is done. That fixes the
 * premature reveal, the duplicate, and the false promise in one move, and it is what both design
 * reviews asked for when they said to merge the result screen into the terminal one.
 *
 * 🔴 M3 IS IN, AND IT IS THE WHOLE POINT (Jay, 2026-08-26). Two independent design reviews reached the
 * same verdict about the first cut: the page promised a "project" and delivered a personality quiz.
 * Eight swipes and four sliders is copy ABOUT a job, not the job. `m3-activity` is the first step where
 * a reader does something with a consequence: read an operating table, compute three rates, and find
 * that every quality gauge decayed while the topline tripled. That is the taste of the career the
 * surface exists to give.
 *
 * `m3-intro` is deliberately skipped and `m3-reading` kept: the intro frames, the reading screen
 * teaches, and only one of those is worth a beat to someone who arrived from a search result.
 *
 * WHY THESE STEPS, AND NOT MORE. Measured on the post-doors cohort with a 2-hour conversion window:
 * welcome → `m2-results` (the archetype reveal) takes **202 seconds**; welcome → the end takes **982**.
 * M3 and M4 are therefore ~80% of the runtime, and they buy only about 10 points of completion. A
 * subscriber mid-trial will spend that; a cold reader who arrived on an internship guide from Google
 * will not. So the preview ends at the reveal, which lands the whole thing at roughly 4-5 minutes
 * including the payoff, deliberately at the short end of the 5-7 minute target, because the failure
 * mode with a cold audience is length, not brevity.
 *
 * `m1-complete` is deliberately ABSENT. It is a full-screen green tick that auto-advances after about
 * two seconds with no control on it at all. In the sprint that is a earned beat between four modules.
 * Here it is a forced pause in a nine-step flow, and both design reviews independently called it the
 * moment the thing stops feeling like a page you found on Google and starts feeling like a course you
 * have been enrolled in. Removing it also removes one of only two steps with nothing to do.
 *
 * 🔴 THE IDS ARE REUSED ON PURPOSE, AND `recommendations` IS LOAD-BEARING. Step ids are the analytics
 * vocabulary (see the sprint's `steps.ts`), so reusing them makes "how far did a cold reader get" and
 * "how far did a trialer get" the same question over the same field. `recommendations` is the sprint's
 * TERMINAL id, so reusing it here as the payoff slot means:
 *   - `moduleProgressAt` already scores it 100%, and `progressHiddenByDefault` already hides the bar;
 *   - the preview needs NO new step id, so the sprint's exhaustive `Record<Step, ...>` maps and its
 *     `never`-checked switch are untouched;
 *   - "reached the terminal step" is one filter across both surfaces.
 *
 * The COMPONENT behind it differs (see `preview-registry.ts`). The sprint renders its recommendations
 * screen; the preview renders the payoff. Same slot, different screen.
 */
export const PREVIEW_STEPS = [
  'welcome',
  'm1-intro',
  'm1-activity',
  'm1-results',
  'm2-intro',
  'm2-activity',
  'm3-reading',
  'm3-activity',
  'recommendations',
] as const satisfies readonly Step[];

export type PreviewStep = (typeof PREVIEW_STEPS)[number];

/**
 * How many modules the preview runs. Feeds the progress bar's DENOMINATOR through `LabsSurface`, so
 * `m2-results` reads 2-of-2 (100%) rather than the 2-of-4 (50%) it would show if it were still being
 * scored against the four modules the preview never reaches.
 */
export const PREVIEW_MODULE_COUNT = 3;

/**
 * Distinguishes the preview's persisted draft and cached store from the sprint's, for the same pack.
 * Without it a reader who previewed and later subscribed would resume the full sprint at whatever step
 * the preview left behind.
 */
export const PREVIEW_STORE_VARIANT = 'preview';

/** The terminal step, the payoff and email capture. Reaching it IS completing the preview. */
export const PREVIEW_TERMINAL_STEP: PreviewStep = 'recommendations';
